import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, RefreshCw, LifeBuoy } from 'lucide-react';
import { listTickets, type Ticket } from '@/api/support';
import { toUserMessage } from '@/lib/errors';
import { Button } from '@/components/assist/ui/button';
import { Card } from '@/components/assist/ui/card';
import { Skeleton } from '@/components/assist/ui/skeleton';
import { useSupportDrawer } from '@/contexts/SupportDrawerContext';

const STATUS_COLORS: Record<string, string> = {
  'Open': 'bg-[hsl(var(--status-healthy)/0.15)] text-status-healthy',
  'Waiting for support': 'bg-[hsl(var(--status-attention)/0.15)] text-status-attention',
  'In Progress': 'bg-[hsl(var(--primary)/0.15)] text-primary',
  'Resolved': 'bg-[hsl(var(--status-thriving)/0.15)] text-status-thriving',
  'Closed': 'bg-muted text-muted-foreground',
};

function getStatusColor(status: string) {
  return STATUS_COLORS[status] || 'bg-muted text-muted-foreground';
}

function TicketListSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-4 rounded-lg border border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/4" />
            </div>
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function TicketList() {
  const { t } = useTranslation('assist');
  const { setView, openTicketDetail } = useSupportDrawer();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTickets();
      setTickets(data.tickets);
    } catch (e) {
      setError(toUserMessage(e, t, { context: 'loadTickets' }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-medium text-muted-foreground">
          {t('support.ticketList.title', 'Your Tickets')}
        </p>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={fetchTickets}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setView('create')}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading && <TicketListSkeleton />}

      {error && (
        <div className="text-sm text-destructive py-4 text-center">{error}</div>
      )}

      {!loading && !error && tickets.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
            <LifeBuoy className="h-6 w-6 text-muted-foreground" />
          </div>
          <h4 className="text-base font-semibold mb-1">
            {t('support.ticketList.emptyTitle', 'No tickets yet')}
          </h4>
          <p className="text-sm text-muted-foreground mb-4 max-w-[250px]">
            {t('support.ticketList.emptyDescription', 'Create a ticket to get help from our support team.')}
          </p>
          <Button variant="outline" size="sm" onClick={() => setView('create')}>
            <Plus className="mr-2 h-4 w-4" />
            {t('support.ticketList.createFirst', 'Create your first ticket')}
          </Button>
        </div>
      )}

      {!loading && tickets.length > 0 && (
        <div className="space-y-2 overflow-y-auto flex-1 pr-1">
          {tickets.map((ticket, index) => (
            <Card
              key={ticket.key}
              role="button"
              tabIndex={0}
              className="shadow-card hover-lift cursor-pointer opacity-0 animate-fade-in-up"
              style={{ animationDelay: `${index * 0.05}s`, animationFillMode: 'forwards' }}
              onClick={() => openTicketDetail(ticket.key)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openTicketDetail(ticket.key); } }}
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{ticket.summary}</p>
                    <p className="text-xs text-muted-foreground mt-1">{ticket.key}</p>
                  </div>
                  <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap font-medium ${getStatusColor(ticket.status)}`}>
                    {ticket.status}
                  </span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
